import I from 'components/I'
import { useContext } from 'react'
import Table from 'react-bootstrap/Table'
import { Trans, useTranslation } from 'react-i18next'
import OrderContext from './_context'
import FooterRow from './FooterRow'
import ItemRow from './ItemRow'

export default function ItemsTable() {
  const { t } = useTranslation()
  const order = useContext(OrderContext)

   // Available scenarios:
   // ✓ single item  &  no custom coupon  =  only subtotal
   // ✓ single item  &  custom coupon     =  coupon and then subtotal
   // ✓ many items   &  no custom coupon  =  qty and subtotal
   // ✓ many items   &  custom coupon     =  qty and coupon, then subtotal

  /** A single item never needs an extra totals line, even when its qty > 1. However, many items will always need.
   * @type {boolean} */
  const showCount          = order.items.length > 1
  const showCustomCoupon   = order.coupon_id > 0 && order.coupon_amount == 0
  const couponTitle        = <><I force>{order.coupon_name}</I> {order.coupon_code && <tt className="coupon">{order.coupon_code}</tt>}</>

  let firstFooterRows    = []
  if (showCustomCoupon) {
    firstFooterRows[0] = { title: couponTitle, icon: 'ticket-alt', show: true } //this row is optional
  }
  firstFooterRows.push({ show: true, title: t('Subtotal'), value: order.subtotal }) //but the subtotal must always show

  //NOTE: the common idiom {bool && <El/>} causes a warning inside table elements, we need an explicit null ¯\_(ツ)_/¯
  return <Table className={'mb-2' /* just enough so the total doesn't go into the bottom shadow on large orders */}>
    <thead className="default-header">
      <tr>
        <th colSpan={2}>{t('Item')}</th>
        <th>{t('Price')}</th>
      </tr>
    </thead>

    <tbody>
      {order.items.map(item => <ItemRow item={item} key={item.id} />)}
    </tbody>

    <tfoot>
      <tr>
        {!showCount? null : <td key="count" width={1} className="text-nowrap">
          <span className="qty"><Trans count={order.itemsCount}><tt>{{ count: order.itemsCount }}</tt> item in total</Trans></span>
        </td>}
        <FooterRow skipTR colSpan={showCount? 1 : 2} {...firstFooterRows[0]}/>{/* either custom coupon or subtotal */}
      </tr>

      {order.coupon_id <= 0 || !order.coupon_free_item? null : <tr>
        <th colSpan={3}><Trans>Free item: <b>{{ item: order.coupon_free_item }}</b></Trans></th>
      </tr>}

      {firstFooterRows[1] && <FooterRow {...firstFooterRows[1]}/> /* if present, this will always be the subtotal */}

	    {order.coupon_can_be_used_for_all ? null: <FooterRow title={couponTitle} icon="ticket-alt"
        show={order.coupon_id > 0 && order.coupon_amount > 0} value={-order.coupon_amount} />}

	    {/** FIXME wow, find a way to DRY this! taxable fees show above the taxes, non-taxable show below . Types are missing as well */}
      {order.delivery_charge_is_taxable ? <FooterRow title={order.restaurant.delivery_fee_name || t('Delivery')} value={order.delivery_charge_amount} /> : null}
      {order.additional_fee_taxable ? <FooterRow title={order.restaurant.per_order_surcharge_name || t('Processing Fee')}
        rate={order.restaurant.per_order_surcharge_type == 2? order.restaurant.per_order_surcharge : null}
        value={order.additional_fee} hide={!order.additional_fee}/> : null}
      {order.fees.map(([index, fee]) => (<FooterRow title={fee.fee_option_name} value={fee.fee} hide={!fee.is_taxable} />))}
      {order.is_bag_fee_taxable ? <FooterRow title={order.bags_type || t('Bags Fee')} show={order.bags_fee} value={order.bags_fee} /> : null}

      <FooterRow title={order.tax_label || t('Tax')} rate={order.tax_rate} value={order.tax_amount} />
      <FooterRow title={order.tax_label_2 || t('Additional Tax')} rate={order.tax_rate_2} value={order.tax_amount_2} />
      {Object.entries(order.item_tax_rates).map(([title, value]) => <FooterRow {...{ title, value }} />)}

      {!order.delivery_charge_is_taxable ? <FooterRow title={order.restaurant.delivery_fee_name || t('Delivery')} value={order.delivery_charge_amount} /> : null}
      {!order.additional_fee_taxable ? <FooterRow title={order.restaurant.per_order_surcharge_name || t('Processing Fee')}
		      rate={order.restaurant.per_order_surcharge_type == 2? order.restaurant.per_order_surcharge : null}
		      value={order.additional_fee} hide={!order.additional_fee}/> : null}
      {order.fees.map(([index, fee]) => (<FooterRow title={fee.fee_option_name} value={fee.fee} hide={fee.is_taxable} />))}
      {!order.is_bag_fee_taxable ? <FooterRow title={order.bags_type || t('Bags Fee')} show={order.bags_fee} value={order.bags_fee} /> : null}

      <FooterRow title={order.bags_type || t('Bags Fee')} show={order.bags_fee} value={order.bags_fee} />

      <FooterRow show={order.tip_text != null} title={t('Tip')} value={order.tip_formatted} />

	    <FooterRow title={order.restaurant.cc_fee_name || t('Credit Card Fee')} show={order.cc_fee} value={order.cc_fee} />

	    {order.coupon_can_be_used_for_all ? <FooterRow title={couponTitle} icon="ticket-alt"
				show={order.coupon_id > 0 && order.coupon_amount > 0} value={-order.coupon_amount} />: null}

      <FooterRow show title={t('Total')} badge={<order.comp.pmtStatus showPaid/>} value={order.total_price} width={1}/>
    </tfoot>
  </Table>
}
